import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  LineChart,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import Logo from "../components/Logo";
import {
  Trophy,
  Medal,
  Fire,
  User as UserIcon,
  Barbell,
  Warning,
  Share as ShareIcon,
  Copy as CopyIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEST_META = {
  yoyo: { label: "Resistencia", accent: "#FF3B30" },
  t1000: { label: "1000m", accent: "#FF9F0A" },
  speed: { label: "Velocidad", accent: "#32D74B" },
  imtp: { label: "Fuerza", accent: "#007AFF" },
  jumps: { label: "Salto", accent: "#BF5AF2" },
  fms: { label: "Movilidad (FMS)", accent: "#64D2FF" },
};

const TIER_STYLES = {
  gold:   { bg: "linear-gradient(135deg,#FFD60A 0%,#FF9F0A 100%)", color: "#0A0A0A" },
  silver: { bg: "linear-gradient(135deg,#E5E5EA 0%,#AEAEB2 100%)", color: "#0A0A0A" },
  bronze: { bg: "linear-gradient(135deg,#D98A5C 0%,#A0522D 100%)", color: "#FFFFFF" },
};

function BadgePill({ badge }) {
  const s = TIER_STYLES[badge.tier] || TIER_STYLES.bronze;
  return (
    <div
      className="flex items-center gap-2 px-3 py-2 border border-app"
      style={{ background: s.bg, color: s.color }}
      data-testid={`badge-${badge.key}`}
    >
      <Medal size={14} weight="fill" />
      <div>
        <div className="text-[10px] uppercase tracking-widest font-black leading-none">{badge.label}</div>
        <div className="text-[9px] opacity-80 leading-tight mt-0.5">{badge.description}</div>
      </div>
    </div>
  );
}

function BestMarkCard({ testKey, mark, evolution }) {
  const meta = TEST_META[testKey] || { label: testKey.toUpperCase(), accent: "#A3A3A3" };
  return (
    <div className="bg-[#0A0A0A] border border-app p-4 relative overflow-hidden" data-testid={`best-${testKey}`}>
      <div className="absolute top-0 right-0 w-1 h-full" style={{ background: meta.accent }} />
      <div className="flex items-center gap-2 mb-2">
        <Trophy size={14} weight="fill" style={{ color: meta.accent }} />
        <div className="text-[10px] uppercase tracking-widest text-muted font-bold">{meta.label}</div>
      </div>
      <div className="text-[9px] uppercase tracking-widest text-muted mb-1">Best: {mark.label}</div>
      <div className="font-display font-black text-3xl tracking-tight leading-none mb-2">{mark.value}</div>
      {mark.date && (
        <div className="text-[10px] text-muted">
          {new Date(mark.date).toLocaleDateString("es-AR", { month: "short", year: "numeric" })}
        </div>
      )}
      {evolution && evolution.length >= 2 && (
        <div className="mt-3 h-12 -mx-1" data-testid={`evolution-${testKey}`}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={evolution}>
              <XAxis dataKey="date" hide />
              <YAxis hide domain={["auto", "auto"]} />
              <Tooltip
                contentStyle={{
                  background: "#141414",
                  border: "1px solid #262626",
                  fontSize: 10,
                }}
                labelFormatter={(d) => new Date(d).toLocaleDateString("es-AR")}
                formatter={(v) => [v.toFixed(1), "Valor"]}
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke={meta.accent}
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export default function AthleteCard() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/public/athletes/${token}`)
      .then((r) => setData(r.data))
      .catch((err) => setError(err.response?.data?.detail || "Error al cargar la card"))
      .finally(() => setLoading(false));
  }, [token]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copiado");
    } catch {
      toast.error("No se pudo copiar");
    }
  };
  const shareNative = async () => {
    const url = window.location.href;
    const text = `${data.name} · Athlon Sports`;
    if (navigator.share) {
      try { await navigator.share({ title: text, url }); } catch { /* user canceled */ }
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(`${text} — ${url}`)}`, "_blank");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-app flex items-center justify-center">
        <Logo size={120} className="animate-pulse" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-app flex items-center justify-center p-4">
        <div className="card-base p-10 text-center max-w-md">
          <Warning size={56} weight="duotone" className="text-[#FF3B30] mx-auto mb-4" />
          <h1 className="font-display uppercase font-black text-3xl mb-2">Card no disponible</h1>
          <p className="text-muted text-sm mb-6">{error || "Esta card fue revocada o el link es incorrecto."}</p>
          <Link to="/login" className="btn-primary inline-block">Ir a Athlon Sports</Link>
        </div>
      </div>
    );
  }

  const bestEntries = Object.entries(data.best_marks || {});

  return (
    <div className="min-h-screen bg-app text-white">
      {/* Hero con gradient + grain */}
      <div
        className="relative overflow-hidden border-b border-app"
        style={{
          background:
            "radial-gradient(circle at 20% 0%, rgba(255,59,48,0.18) 0%, transparent 50%), radial-gradient(circle at 80% 100%, rgba(0,122,255,0.12) 0%, transparent 50%), #0A0A0A",
        }}
      >
        <div className="max-w-5xl mx-auto px-5 py-10 sm:py-14 relative z-10">
          <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
            <div className="flex items-center gap-3">
              <Logo size={40} />
              <div>
                <div className="text-[10px] uppercase tracking-[3px] text-muted">Athlon Sports</div>
                <div className="font-display font-black uppercase text-sm tracking-tight">Athlete Card</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={copyLink} className="btn-secondary !py-2 !px-3 text-xs flex items-center gap-1" data-testid="card-copy-link">
                <CopyIcon size={12} weight="bold" /> Copiar link
              </button>
              <button onClick={shareNative} className="btn-primary !py-2 !px-3 text-xs flex items-center gap-1" data-testid="card-share">
                <ShareIcon size={12} weight="bold" /> Compartir
              </button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-5 sm:gap-7">
            <div className="w-28 h-28 sm:w-36 sm:h-36 bg-[#0A0A0A] border-2 border-app flex items-center justify-center overflow-hidden flex-shrink-0">
              {data.avatar_url ? (
                <img src={data.avatar_url} alt={data.name} className="w-full h-full object-cover" />
              ) : (
                <UserIcon size={56} weight="bold" className="text-muted" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] uppercase tracking-[4px] text-primary-app font-bold mb-1">{data.sport || "Atleta"}</div>
              <h1 className="font-display font-black uppercase text-4xl sm:text-6xl tracking-tighter leading-[0.9] break-words" data-testid="card-name">
                {data.name}
              </h1>
              <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-sm text-muted">
                {data.position && <span>{data.position}</span>}
                {data.age && <span>{data.age} años</span>}
                {data.height_cm && <span>{data.height_cm} cm</span>}
                {data.weight_kg && <span>{data.weight_kg} kg</span>}
              </div>
            </div>
          </div>

          {/* Badges row */}
          {(data.badges || []).length > 0 && (
            <div className="mt-8 flex flex-wrap gap-2" data-testid="card-badges">
              {data.badges.map((b) => <BadgePill key={b.key} badge={b} />)}
            </div>
          )}
        </div>
      </div>

      {/* Stats grid */}
      <div className="max-w-5xl mx-auto px-5 py-10">
        {bestEntries.length === 0 ? (
          <div className="card-base p-10 text-center">
            <Barbell size={40} weight="duotone" className="text-muted mx-auto mb-3" />
            <div className="font-display uppercase font-black text-xl mb-1">Sin testeos aún</div>
            <div className="text-sm text-muted">Cuando el atleta registre sus primeros testeos, aparecerán acá.</div>
          </div>
        ) : (
          <>
            <div className="flex items-baseline gap-2 mb-5">
              <h2 className="font-display font-black uppercase text-2xl tracking-tighter">Mejores marcas</h2>
              <span className="text-[10px] uppercase tracking-widest text-muted">· histórico</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {bestEntries.map(([key, mark]) => (
                <BestMarkCard key={key} testKey={key} mark={mark} evolution={data.evolution?.[key]} />
              ))}
            </div>
          </>
        )}

        <div className="mt-10 flex items-center gap-2 text-xs text-muted">
          <Fire size={14} weight="fill" className="text-primary-app" />
          <span>{data.sessions_count} sesiones completadas · performance tracking by Athlon Sports</span>
        </div>
      </div>
    </div>
  );
}
